import React from 'react';
import { createPortal } from 'react-dom';
import { Box, Typography, Tooltip, Skeleton, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ledger, FONT_DISPLAY } from '../../theme/modernTheme';
import { getCurrencySymbol, placeCurrencySymbol } from '../../utils/currency';
import { getLocale } from '../../utils/locale';
import i18n from '../../i18n';

// Shared building blocks of the "Ledger" design system (.interface-design/system.md).

// ---------- number formatting ----------

// Formatters are built once per UI language (Intl.NumberFormat construction is not free)
const fmtCache = new Map<string, Intl.NumberFormat>();
function numFmt(name: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const locale = getLocale();
  const key = `${locale}|${name}`;
  let f = fmtCache.get(key);
  if (!f) { f = new Intl.NumberFormat(locale, options); fmtCache.set(key, f); }
  return f;
}
const compactFmt = () => numFmt('compact', { notation: 'compact', maximumFractionDigits: 1 });
const smallFmt = () => numFmt('small', { maximumFractionDigits: 2 });
const midFmt = () => numFmt('mid', { maximumFractionDigits: 1 });

/** 1.234 / 12,3 B / 4,3 Mn — full digits below 100.000, compact above. Fractions are kept
 *  (conversions, frequency and ratios are often fractional): 2 digits below 100, 1 below 100.000. */
export function fmtNumber(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '–';
  const a = Math.abs(v);
  if (a >= 100000) return compactFmt().format(v);
  return a < 100 ? smallFmt().format(v) : midFmt().format(v);
}

/** Money with the currency symbol on the same line; compact above 100.000 */
export function fmtMoney(v: number | null | undefined, currency = 'TRY'): string {
  if (v == null || Number.isNaN(v)) return '–';
  const sym = getCurrencySymbol(currency);
  const body = Math.abs(v) >= 100000
    ? compactFmt().format(v)
    : Math.abs(v) < 1000
      ? numFmt('money2', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)
      : numFmt('money0', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
  return placeCurrencySymbol(body, sym, '\u00a0');
}

// The text Intl puts before and after the number in a percentage: "%" / "" in Turkish,
// "" / "%" in English, "" / " %" in German
const percentAffixes = new Map<string, [string, string]>();
function percentAffix(locale: string): [string, string] {
  let affix = percentAffixes.get(locale);
  if (!affix) {
    const parts = new Intl.NumberFormat(locale, { style: 'percent' }).formatToParts(0.5);
    const first = parts.findIndex((p) => p.type === 'integer');
    const text = (list: Intl.NumberFormatPart[]) => list.filter((p) => p.type === 'percentSign' || p.type === 'literal').map((p) => p.value).join('');
    affix = [text(parts.slice(0, first)), text(parts.slice(first + 1))];
    percentAffixes.set(locale, affix);
  }
  return affix;
}

/** A number with exactly `digits` decimals in the UI language ("325,0" / "325.0"), e.g. sizes */
export function fmtDecimal(v: number | null | undefined, digits = 1): string {
  if (v == null || Number.isNaN(v)) return '–';
  return v.toLocaleString(getLocale(), { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** An already formatted number with the percent sign placed the UI language's way ("%12,5" / "12.5%") */
export function withPercent(body: string): string {
  const [before, after] = percentAffix(getLocale());
  return `${before}${body}${after}`;
}

export function fmtPercent(v: number | null | undefined, digits = 1): string {
  if (v == null || Number.isNaN(v)) return '–';
  return withPercent(v.toLocaleString(getLocale(), { minimumFractionDigits: digits, maximumFractionDigits: digits }));
}

// ---------- page header ----------

// Set while a page is rendered as a tab of another page (see TabbedPage): its own PageHeader
// then renders only its actions, into the host header's slot next to the view switch, because
// the host page already carries the title. `slot` is null until the host header has mounted.
const EmbeddedPageContext = React.createContext<{ slot: HTMLElement | null } | null>(null);
export const EmbeddedPage: React.FC<{ children: React.ReactNode; slot: HTMLElement | null }> = ({ children, slot }) => (
  <EmbeddedPageContext.Provider value={{ slot }}>{children}</EmbeddedPageContext.Provider>
);

export const PageHeader: React.FC<{ title: string; description?: React.ReactNode; actions?: React.ReactNode }> = ({ title, description, actions }) => {
  const L = ledger(useTheme());
  const embedded = React.useContext(EmbeddedPageContext);
  if (embedded) {
    return actions && embedded.slot ? createPortal(actions, embedded.slot) : null;
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h4" component="h1" sx={{ color: L.ink, textWrap: 'balance' } as any}>{title}</Typography>
        {description && (
          <Typography variant="body2" sx={{ color: L.inkTertiary, mt: 0.5, maxWidth: 720 }}>{description}</Typography>
        )}
      </Box>
      {actions && <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', minWidth: 0 }}>{actions}</Box>}
    </Box>
  );
};

// ---------- segmented control ----------

/** Compact view switch (as on Budget Pacing): the standard way to switch views within a page */
export function SegmentedControl<T extends string>({ value, options, onChange, ariaLabel }: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  ariaLabel?: string;
}) {
  return (
    <ToggleButtonGroup value={value} exclusive size="small" aria-label={ariaLabel} onChange={(_, v) => { if (v) onChange(v); }}>
      {/* 34px: the height of the buttons and icon buttons that sit beside it in page headers */}
      {options.map(o => <ToggleButton key={o.value} value={o.value} sx={{ px: 1.5, height: 34, whiteSpace: 'nowrap' }}>{o.label}</ToggleButton>)}
    </ToggleButtonGroup>
  );
}

// ---------- KPI strip ----------

/** Which direction is good for a metric: cost going up is bad, conversions going up is good */
export type Polarity = 'up' | 'down' | 'neutral';

export interface KpiItem {
  label: string;
  value: string;
  /** Change as a fraction (0.12 = +12%) */
  delta?: number | null;
  /** e.g. "önceki 30 güne göre" */
  deltaLabel?: string;
  polarity?: Polarity;
  /** small line under the value, e.g. "1.062 kampanyadan" */
  hint?: string;
  spark?: number[];
  tooltip?: string;
}

const Sparkline: React.FC<{ data: number[] }> = ({ data }) => {
  const L = ledger(useTheme());
  if (data.length < 2) return null;
  const w = 72, h = 22;
  const min = Math.min(...data), max = Math.max(...data);
  const span = max - min || 1;
  const pts = data.map((v, i) => [(i / (data.length - 1)) * w, h - 2 - ((v - min) / span) * (h - 4)]);
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const last = pts[pts.length - 1];
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" style={{ display: 'block', overflow: 'visible' }}>
      <path d={d} fill="none" stroke={L.inkMuted} strokeWidth={1.25} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r={2} fill={L.accent} />
    </svg>
  );
};

export const KpiStrip: React.FC<{ items: KpiItem[]; loading?: boolean }> = ({ items, loading }) => {
  const L = ledger(useTheme());
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${Math.min(items.length, 5)}, 1fr)` },
        border: `1px solid ${L.rule}`,
        borderRadius: 1.5,
        backgroundColor: L.surface,
        mb: 2.5,
        overflow: 'hidden',
      }}
    >
      {items.map((k, i) => {
        const deltaColor = k.delta == null || k.polarity === 'neutral' || Math.abs(k.delta) < 0.0005
          ? L.inkTertiary
          : (k.delta > 0) === (k.polarity !== 'down') ? L.gain : L.loss;
        const cell = (
          <Box
            key={k.label}
            sx={{
              p: 2,
              minWidth: 0,
              borderLeft: { xs: i % 2 ? `1px solid ${L.ruleSoft}` : 'none', md: i ? `1px solid ${L.ruleSoft}` : 'none' },
              borderTop: { xs: i >= 2 ? `1px solid ${L.ruleSoft}` : 'none', md: 'none' },
            }}
          >
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: L.inkTertiary, mb: 0.75 }} noWrap>{k.label}</Typography>
            {loading ? (
              // Same height as the loaded cell (value + note line), so nothing below jumps when data lands
              <>
                <Skeleton variant="text" width="60%" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' }, lineHeight: 1.15 }} />
                {(k.delta !== undefined || k.hint) && <Skeleton variant="text" width="45%" sx={{ fontSize: '0.75rem', mt: 0.5 }} />}
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1 }}>
                <Typography
                  sx={{ fontFamily: FONT_DISPLAY, fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.15, color: L.ink, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                >
                  {k.value}
                </Typography>
                {k.spark && <Box sx={{ display: { xs: 'none', lg: 'block' }, mb: 0.25 }}><Sparkline data={k.spark} /></Box>}
              </Box>
            )}
            {!loading && (k.delta != null || k.hint) && (
              <Typography sx={{ fontSize: '0.75rem', mt: 0.5, color: L.inkTertiary, fontVariantNumeric: 'tabular-nums' }} noWrap>
                {k.delta != null && (
                  <Box component="span" sx={{ color: deltaColor, fontWeight: 500 }}>
                    {k.delta > 0 ? '↑' : k.delta < 0 ? '↓' : ''} {fmtPercent(Math.abs(k.delta) * 100)}
                  </Box>
                )}
                {k.delta != null && k.deltaLabel ? ` ${k.deltaLabel}` : ''}
                {k.hint && <>{k.delta != null ? ' · ' : ''}{k.hint}</>}
              </Typography>
            )}
          </Box>
        );
        return k.tooltip ? <Tooltip key={k.label} title={k.tooltip}>{cell}</Tooltip> : cell;
      })}
    </Box>
  );
};

// ---------- status ----------

export type StatusTone = 'positive' | 'neutral' | 'warning' | 'negative' | 'info';

export const StatusDot: React.FC<{ tone: StatusTone; label: string }> = ({ tone, label }) => {
  const L = ledger(useTheme());
  const color = { positive: L.gain, neutral: L.inkMuted, warning: L.alert, negative: L.loss, info: L.accent }[tone];
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap', fontSize: '0.78125rem', color: L.inkSecondary }}>
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
      {label}
    </Box>
  );
};

/** Google Ads entity status -> tone + Turkish label */
export function adsStatus(status?: string | null): { tone: StatusTone; label: string } {
  switch ((status || '').toUpperCase()) {
    case 'ENABLED': return { tone: 'positive', label: i18n.t('Aktif') };
    case 'PAUSED': return { tone: 'neutral', label: i18n.t('Duraklatıldı') };
    case 'REMOVED': return { tone: 'negative', label: i18n.t('Kaldırıldı') };
    default: return { tone: 'neutral', label: status || '–' };
  }
}

// ---------- empty state ----------

/**
 * A colour the user picked for their own data (a WhatsApp label, a department): shown as a small
 * dot beside the name. Unlike decorative per-item colour this is information, so it stays.
 */
export const ColorDot: React.FC<{ color?: string | null; size?: number }> = ({ color, size = 8 }) => (
  color ? <Box component="span" aria-hidden="true" sx={{ display: 'inline-block', width: size, height: size, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} /> : null
);

/** A name with its data colour dot in front, for chip labels and list rows */
export const ColorLabel: React.FC<{ color?: string | null; children: React.ReactNode }> = ({ color, children }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0, maxWidth: '100%', verticalAlign: 'bottom' }}>
    <ColorDot color={color} />
    <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children}</Box>
  </Box>
);

export const EmptyState: React.FC<{ title: string; description?: string; action?: React.ReactNode }> = ({ title, description, action }) => {
  const L = ledger(useTheme());
  return (
    <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: L.ink }}>{title}</Typography>
      {description && <Typography sx={{ fontSize: '0.8125rem', color: L.inkTertiary, mt: 0.5, maxWidth: 420, mx: 'auto' }}>{description}</Typography>}
      {action && <Box sx={{ mt: 2 }}>{action}</Box>}
    </Box>
  );
};

// ---------- panel ----------

/** Flat bordered surface; the standard container for tables and sections */
export const Panel: React.FC<{ children: React.ReactNode; title?: string; actions?: React.ReactNode; sx?: any; padded?: boolean }> = ({ children, title, actions, sx, padded }) => {
  const L = ledger(useTheme());
  return (
    <Box sx={{ border: `1px solid ${L.rule}`, borderRadius: 1.5, backgroundColor: L.surface, overflow: 'hidden', ...sx }}>
      {(title || actions) && (
        <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, borderBottom: `1px solid ${L.ruleSoft}` }}>
          {title && <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: L.ink }}>{title}</Typography>}
          {actions}
        </Box>
      )}
      <Box sx={padded ? { p: 2 } : undefined}>{children}</Box>
    </Box>
  );
};
